/**
 * Maps English enum values for structured job fields to their Finnish and Swedish
 * equivalents, matching the enums defined in the processor schema.
 *
 * Used in transformJobForLanguage to translate fallback English enum values into
 * the correct locale rather than surfacing raw English strings on non-English pages.
 */

type LocaleMap = Record<string, string>;

export interface EnumTranslationMap {
  en_to_fi: LocaleMap;
  en_to_sv: LocaleMap;
}

export const JOB_TYPE_TRANSLATIONS: EnumTranslationMap = {
  en_to_fi: {
    'Full-time': 'Kokoaikainen',
    'Part-time': 'Osa-aikainen',
    Internship: 'Harjoittelupaikka',
    Temporary: 'Määräaikainen',
    'Summer Job': 'Kesätyö',
    'Open Application': 'Avoin hakemus',
    Contract: 'Sopimuspohjainen työ',
    Freelance: 'Keikkatyö',
    Apprenticeship: 'Oppisopimus',
    // Legacy English spellings from older processor output
    'Summer job': 'Kesätyö',
    'Open application': 'Avoin hakemus',
  },
  en_to_sv: {
    'Full-time': 'Heltid',
    'Part-time': 'Deltid',
    Internship: 'Praktik',
    Temporary: 'Tillfällig',
    'Summer Job': 'Sommarjobb',
    'Open Application': 'Öppen ansökan',
    Contract: 'Uppdrag',
    Freelance: 'Frilans',
    Apprenticeship: 'Läroavtal',
    'Summer job': 'Sommarjobb',
    'Open application': 'Öppen ansökan',
  },
};

export const ROLE_LEVEL_TRANSLATIONS: EnumTranslationMap = {
  en_to_fi: {
    'Intern': 'Harjoittelija',
    'Junior': 'Juniori',
    'Mid-Level': 'Keskitaso',
    'Senior': 'Senior-taso',
    'Expert': 'Asiantuntija',
    'Manager': 'Esimies',
    'Director': 'Johtaja',
    'Executive': 'Ylin johto',
  },
  en_to_sv: {
    'Intern': 'Praktikant',
    'Junior': 'Junior',
    'Mid-Level': 'Medelnivå',
    'Senior': 'Senior',
    'Expert': 'Expert',
    'Manager': 'Chef',
    'Director': 'Direktör',
    'Executive': 'Företagsledare',
  },
};

export const PEOPLE_MANAGEMENT_TRANSLATIONS: EnumTranslationMap = {
  en_to_fi: {
    'No People Management': 'Ei esimiestehtäviä',
    'People Manager': 'Esimies',
    'Manager of Managers': 'Esimiesten esimies',
  },
  en_to_sv: {
    'No People Management': 'Inget personalansvar',
    'People Manager': 'Chef med personalansvar',
    'Manager of Managers': 'Chef över chefer',
  },
};

export const REMOTENESS_TRANSLATIONS: EnumTranslationMap = {
  en_to_fi: {
    'Remote': 'Etätyö',
    'Hybrid': 'Hybridityö',
    'Onsite': 'Paikkasidonnainen työ',
  },
  en_to_sv: {
    'Remote': 'Distans',
    'Hybrid': 'Hybrid',
    'Onsite': 'På plats',
  },
};

/**
 * Map of field base names to their enum translation tables.
 * Only fields that have locale-specific enum values need entries here.
 */
export const ENUM_FIELD_TRANSLATIONS: Record<string, EnumTranslationMap> = {
  jobType: JOB_TYPE_TRANSLATIONS,
  normalisedRoleLevel: ROLE_LEVEL_TRANSLATIONS,
  peopleManagementTier: PEOPLE_MANAGEMENT_TRANSLATIONS,
  remoteness: REMOTENESS_TRANSLATIONS,
};

/**
 * Translates an English enum value to the target locale using the provided map.
 * Returns the original value if no translation exists.
 */
export function translateEnumValue(
  value: string,
  targetLang: string,
  map: EnumTranslationMap,
): string {
  if (targetLang === 'fi') return map.en_to_fi[value] ?? value;
  if (targetLang === 'sv') return map.en_to_sv[value] ?? value;
  return value;
}
